import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import { DateTime } from 'luxon'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import useIconAccessor from 'components/lesson-type-category/icons'
import { isJuniorAgeGroupEvent } from 'utils/groupEventUtils'
import useStyles from './styles'

const LESSON_LIMIT = 4

const getDurationMinutes = (startDateTime, endDateTime) =>
  Math.round(
    DateTime.fromISO(endDateTime)
      .diff(DateTime.fromISO(startDateTime), 'minutes')
      .toObject().minutes,
  )

const getLessonRow = (booking, iconAccessor) => {
  const start = DateTime.fromISO(booking.startDateTime).setZone(
    booking.timezone,
  )

  if (booking.__typename === 'Session') {
    const student = booking.enrollments[0]?.student
    const category = booking.lessonType?.category
    const categoryMeta = category ? iconAccessor[category] : null
    const fullName =
      student && `${student.firstName} ${student.lastName}`.trim()
    const duration =
      booking.lessonType?.durationInMinutes ||
      getDurationMinutes(booking.startDateTime, booking.endDateTime)

    return {
      key: `${booking.id}-${booking.startDateTime}`,
      name: fullName || booking.title,
      meta: `${categoryMeta?.text || booking.title} · ${duration}m`,
      time: start.toFormat('ccc h:mm a'),
    }
  }

  const duration = getDurationMinutes(
    booking.startDateTime,
    booking.endDateTime,
  )
  const isJunior = isJuniorAgeGroupEvent(booking.tags || [])

  return {
    key: `${booking.id}-${booking.startDateTime}`,
    name: booking.title,
    meta: `${isJunior ? 'Junior Group' : 'Group Event'} · ${duration}m`,
    time: start.toFormat('ccc h:mm a'),
  }
}

const UpcomingLessons = ({ lessons, loading }) => {
  const classes = useStyles()
  const iconAccessor = useIconAccessor()
  const rows = lessons
    .slice(0, LESSON_LIMIT)
    .map(lesson => getLessonRow(lesson, iconAccessor))

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={classes.iconCircle}>
            <EventAvailableOutlinedIcon />
          </Box>
          <SectionTitle className={classes.cardTitle}>
            Upcoming lessons
          </SectionTitle>
        </Box>
        <Button
          component={RouterLink}
          to="/pga-coach/bookings/schedule"
          className={classes.cardAction}
        >
          View schedule
        </Button>
      </Box>

      {loading ? (
        <LinearProgress color="primary" className={classes.loading} />
      ) : rows.length > 0 ? (
        <Box className={classes.list}>
          {rows.map(row => (
            <Box
              key={row.key}
              component={RouterLink}
              to="/pga-coach/bookings/schedule"
              className={classes.row}
            >
              <Box className={classes.rowMain}>
                <Typography component="span" className={classes.rowName}>
                  {row.name}
                </Typography>
                <Typography component="div" className={classes.rowMeta}>
                  {row.meta}
                </Typography>
              </Box>
              <Box className={classes.rowTrailing}>
                <Typography component="span" className={classes.rowTime}>
                  {row.time}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography className={classes.emptyList}>
          No upcoming lessons scheduled.
        </Typography>
      )}
    </Box>
  )
}

export default UpcomingLessons
